// ---------------------------------------------------------------------------
// imageColorBucket — coarse colour classification of a template thumbnail.
//
// The AI-template gallery's "Colour" filter needs to group templates by their
// dominant tone. Templates have no colour metadata, so we read it straight from
// the thumbnail image: load it (cross-origin) into a tiny canvas, average the
// pixels weighted by saturation, then bucket the result into one of:
//   'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple' — by dominant hue
//   'neutral' — greys / near-black / near-white (low saturation)
//
// Returns null when the image can't be read (CORS taint or a load error) so the
// caller can treat that template as "uncategorised" rather than crash.
// Results are cached per-src so each thumbnail is only sampled once.
// ---------------------------------------------------------------------------

const cache = new Map();

// Map an average hue (0–360°) to a coarse colour name. Ranges are picked so each
// swatch in the gallery filter maps to a visually distinct band of the wheel.
function hueBucket(hue) {
    if (hue < 15 || hue >= 345) return 'red';
    if (hue < 45)  return 'orange';
    if (hue < 70)  return 'yellow';
    if (hue < 165) return 'green';
    if (hue < 255) return 'blue';   // includes cyan/teal
    return 'purple';                // 255–345: violet / magenta / pink
}

function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4; break;
        }
        h *= 60;
    }
    return { h, s, l };
}

export function imageColorBucket(src) {
    if (!src) return Promise.resolve(null);
    if (cache.has(src)) return Promise.resolve(cache.get(src));

    return new Promise((resolve) => {
        const finish = (bucket) => { cache.set(src, bucket); resolve(bucket); };
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const SIZE = 16;
                const canvas = document.createElement('canvas');
                canvas.width = SIZE;
                canvas.height = SIZE;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(img, 0, 0, SIZE, SIZE);
                const { data } = ctx.getImageData(0, 0, SIZE, SIZE);

                // Accumulate hue as unit vectors (avoids the 360°→0° wraparound),
                // weighted by saturation so vivid pixels decide the hue.
                let hx = 0; let hy = 0; let satSum = 0; let lightSum = 0; let n = 0;
                for (let i = 0; i < data.length; i += 4) {
                    if (data[i + 3] < 128) continue; // skip transparent
                    const { h, s, l } = rgbToHsl(data[i], data[i + 1], data[i + 2]);
                    const w = s * s;
                    const rad = (h * Math.PI) / 180;
                    hx += Math.cos(rad) * w;
                    hy += Math.sin(rad) * w;
                    satSum += s;
                    lightSum += l;
                    n += 1;
                }
                if (!n) return finish(null);

                const avgSat = satSum / n;
                const avgLight = lightSum / n;
                if (avgSat < 0.16 || avgLight < 0.1 || avgLight > 0.9) return finish('neutral');

                let hue = (Math.atan2(hy, hx) * 180) / Math.PI;
                if (hue < 0) hue += 360;
                return finish(hueBucket(hue));
            } catch {
                return finish(null); // canvas tainted by cross-origin image
            }
        };
        img.onerror = () => finish(null);
        // Cache-bust so this crossOrigin request is a SEPARATE cache entry from the
        // gallery's display <img> (which loads the same URL without crossOrigin). Reusing
        // that non-CORS cached response would taint the canvas → getImageData throws →
        // null bucket → the colour filter silently matches nothing. A distinct URL forces
        // a fresh CORS fetch that carries Access-Control-Allow-Origin, keeping the canvas clean.
        img.src = src + (src.includes('?') ? '&' : '?') + 'cors=1';
    });
}
